import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../api/client';
import {
  Calendar, Clock, AlertTriangle, User, Activity, MessageSquare,
  Phone, CheckCircle, ChevronRight, ChevronLeft, Users, Shield,
  RefreshCw, Maximize2, X, Video, Home as HomeIcon, Pill
} from 'lucide-react';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CAL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7 AM → 6 PM

const sevCfg = {
  urgent:  { bg: 'bg-red-50',    text: 'text-red-600',     border: 'border-red-100',     dot: 'bg-red-500' },
  high:    { bg: 'bg-orange-50',  text: 'text-orange-600',  border: 'border-orange-100',  dot: 'bg-orange-500' },
  medium:  { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-100',   dot: 'bg-amber-500' },
  low:     { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', dot: 'bg-emerald-500' },
};
const typeIcon = { symptom: Activity, consultation: User, 'follow-up': MessageSquare, appointment: Calendar };

const APPT_CONFIG = {
  virtual_visit:      { label: 'Virtual Visit',       icon: Video,     dot: 'bg-blue-500',   text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  home_visit:         { label: 'Home Visit',          icon: HomeIcon,  dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
  pharmacist_consult: { label: 'Pharmacist Consult',  icon: Pill,      dot: 'bg-amber-500',  text: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
};

const SchedulePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [patients, setPatients] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [priorityPatients, setPriorityPatients] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState('week');
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [scheduleRes, patientsRes, eventsRes, priorityRes, symptomsRes, apptRes] = await Promise.all([
        api.get('/escalation/my-schedule').catch(() => null),
        api.get('/consent/doctor-patients?status=active').catch(() => null),
        api.get('/care-events/recent?hours=48').catch(() => null),
        api.get('/risk-assessments/pending?minScore=30&limit=10').catch(() => null),
        api.get('/symptoms').catch(() => null),
        api.get('/care-events/my-appointments?days=60').catch(() => null),
      ]);
      if (scheduleRes?.data?.success) setSchedule(scheduleRes.data.data);
      if (patientsRes?.data?.data) setPatients(patientsRes.data.data);
      if (eventsRes?.data?.data) setRecentEvents(eventsRes.data.data);
      if (priorityRes?.data?.data) setPriorityPatients(priorityRes.data.data);
      const sData = symptomsRes?.data?.data || symptomsRes?.data || [];
      setSymptoms(Array.isArray(sData) ? sData : []);
      const aData = apptRes?.data?.data || [];
      setAppointments(Array.isArray(aData) ? aData : []);
    } catch (err) {
      console.error('Schedule fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── On-call helpers ──
  const currentDay = new Date().getDay();
  const getShiftForDay = (dayOfWeek) => schedule?.schedule?.shifts?.find(s => s.dayOfWeek === dayOfWeek) || null;
  const isOnCallNow = schedule?.isCurrentlyOnCall;
  const isAlwaysOnCall = schedule?.schedule?.isAlwaysOnCall;
  const isOnRoster = schedule?.isOnCallContact;

  // ── Calendar helpers ──
  const weekStart = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }, [weekOffset]);

  const weekDates = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }), [weekStart]);

  const isToday = (date) => {
    const now = new Date();
    return date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  // Build calendar events from symptoms + scheduled appointments
  const calEvents = useMemo(() => {
    const evts = [];

    symptoms.forEach(s => {
      const created = new Date(s.createdAt);
      evts.push({ id: s._id + '_c', symptomId: s._id, title: s.title, patient: s.userId?.name || 'Patient', severity: s.severity || 'medium', type: 'symptom', date: created, hour: created.getHours() });
      (s.consultingDoctors || []).forEach((d, i) => {
        const dt = new Date(d.startedAt || s.createdAt);
        evts.push({ id: s._id + '_d' + i, symptomId: s._id, title: `Review: ${s.title}`, patient: s.userId?.name || 'Patient', severity: s.severity || 'medium', type: 'consultation', date: dt, hour: dt.getHours() });
      });
      (s.responses || []).forEach((r, i) => {
        const dt = new Date(r.createdAt);
        evts.push({ id: s._id + '_r' + i, symptomId: s._id, title: `Follow-up: ${s.title}`, patient: s.userId?.name || 'Patient', doctor: r.doctorId?.name, severity: s.severity || 'medium', type: 'follow-up', date: dt, hour: dt.getHours() });
      });
    });

    appointments.forEach((appt, i) => {
      const dt = new Date(appt.appointmentData?.scheduledDate || appt.data?.scheduledDate);
      if (isNaN(dt)) return;
      const apptType = appt.appointmentData?.appointmentType || appt.data?.appointmentType || 'virtual_visit';
      const cfg = APPT_CONFIG[apptType] || APPT_CONFIG.virtual_visit;
      evts.push({
        id: appt._id + '_appt',
        appointmentId: appt._id,
        title: cfg.label,
        patient: appt.patientId?.name || 'Patient',
        severity: 'low',
        type: 'appointment',
        apptType,
        meetingLink: appt.appointmentData?.meetingLink || appt.data?.meetingLink,
        notes: appt.appointmentData?.notes || appt.data?.notes,
        date: dt,
        hour: dt.getHours(),
      });
    });

    return evts;
  }, [symptoms, appointments]);

  const getEventsForDay = (date) => calEvents.filter(e => e.date.getDate() === date.getDate() && e.date.getMonth() === date.getMonth() && e.date.getFullYear() === date.getFullYear());
  const getEventsForSlot = (date, hour) => getEventsForDay(date).filter(e => e.hour === hour);

  const weekEvents = useMemo(() => {
    const end = new Date(weekStart); end.setDate(end.getDate() + 7);
    return calEvents.filter(e => e.date >= weekStart && e.date < end);
  }, [calEvents, weekStart]);

  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-2xl animate-pulse bg-slate-100" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">My Schedule</h2>
          <p className="text-slate-500 text-sm mt-1">Shifts, caseload, and activity timeline</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* ═══ ON-CALL BANNER ═══ */}
      {isOnRoster ? (
        <div className={`rounded-2xl p-5 border ${isOnCallNow ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' : 'bg-white border-outline-variant/10'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isOnCallNow ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {isOnCallNow ? <Phone className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="font-bold text-sm text-slate-900">{isOnCallNow ? 'You Are On-Call Now' : 'On-Call Schedule'}</h3>
                <p className="text-xs text-slate-500">
                  {isAlwaysOnCall ? '24/7 Primary On-Call Coverage' : isOnCallNow ? 'Responsible for emergency escalations' : `Role: ${(schedule?.role || 'clinician').replace(/_/g, ' ')}`}
                </p>
              </div>
            </div>
            {isOnCallNow && (
              <span className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Active
              </span>
            )}
          </div>
          {!isAlwaysOnCall && schedule?.schedule?.shifts?.length > 0 && (
            <div className="grid grid-cols-7 gap-2">
              {[0,1,2,3,4,5,6].map(day => {
                const shift = getShiftForDay(day);
                const today = day === currentDay;
                return (
                  <div key={day} className={`rounded-xl p-3 text-center transition-all ${shift ? (today ? 'bg-primary text-white shadow-sm' : 'bg-primary/10 text-primary') : 'bg-slate-50 text-slate-300'}`}>
                    <p className={`text-xs font-bold uppercase tracking-wider ${shift ? (today ? 'text-white/70' : 'text-primary/60') : ''}`}>{DAYS_SHORT[day]}</p>
                    {shift ? (
                      <div className="mt-1">
                        <p className={`text-xs font-bold ${today ? 'text-white' : ''}`}>{shift.startTime}</p>
                        <p className={`text-xs ${today ? 'text-white/70' : 'text-primary/60'}`}>{shift.endTime}</p>
                      </div>
                    ) : <p className="text-xs mt-1 font-medium">Off</p>}
                  </div>
                );
              })}
            </div>
          )}
          {isAlwaysOnCall && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-xl p-3">
              <CheckCircle className="w-4 h-4" /><span className="font-bold">Always on-call — available 24/7 for escalations</span>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-5 border border-outline-variant/10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center"><Clock className="w-5 h-5 text-slate-500" /></div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">No On-Call Shifts</h3>
              <p className="text-xs text-slate-500">Contact your administrator to be added to the on-call roster.</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STATS ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Patients', value: patients.length, icon: Users, color: 'text-primary' },
          { label: 'Priority Cases', value: priorityPatients.filter(p => (p.riskScore?.overall || 0) >= 45).length, icon: AlertTriangle, color: 'text-red-500' },
          { label: 'Appointments', value: appointments.filter(a => new Date(a.appointmentData?.scheduledDate || a.data?.scheduledDate) >= new Date()).length, icon: Calendar, color: 'text-blue-500' },
          { label: 'Events (48h)', value: recentEvents.length, icon: Activity, color: 'text-indigo-500' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-outline-variant/10 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{s.label}</p>
            </div>
            <p className="text-2xl font-extrabold tracking-tighter text-slate-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* ═══ UPCOMING APPOINTMENTS ═══ */}
      {appointments.length > 0 && (() => {
        const upcoming = appointments
          .filter(a => new Date(a.appointmentData?.scheduledDate || a.data?.scheduledDate) >= new Date(Date.now() - 60 * 60 * 1000))
          .sort((a, b) => new Date(a.appointmentData?.scheduledDate || a.data?.scheduledDate) - new Date(b.appointmentData?.scheduledDate || b.data?.scheduledDate))
          .slice(0, 5);
        if (!upcoming.length) return null;
        return (
          <div className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-blue-500" /></div>
              <div>
                <h3 className="font-bold text-sm text-slate-900">Upcoming Appointments</h3>
                <p className="text-xs text-slate-500">{upcoming.length} scheduled</p>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {upcoming.map((appt) => {
                const apptType = appt.appointmentData?.appointmentType || appt.data?.appointmentType || 'virtual_visit';
                const cfg = APPT_CONFIG[apptType] || APPT_CONFIG.virtual_visit;
                const ApptIcon = cfg.icon;
                const dt = new Date(appt.appointmentData?.scheduledDate || appt.data?.scheduledDate);
                const meetingLink = appt.appointmentData?.meetingLink || appt.data?.meetingLink;
                const notes = appt.appointmentData?.notes || appt.data?.notes;
                const patientName = appt.patientId?.name || 'Patient';
                const isToday = dt.toDateString() === new Date().toDateString();
                const isPast = dt < new Date();
                return (
                  <div key={appt._id} className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                      <ApptIcon className={`w-5 h-5 ${cfg.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-bold ${cfg.text}`}>{cfg.label}</p>
                        {isToday && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">Today</span>}
                        {isPast && <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-bold">Past</span>}
                      </div>
                      <p className="text-xs text-slate-700 font-semibold mt-0.5">{patientName}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {notes && <p className="text-xs text-slate-400 italic mt-0.5">{notes}</p>}
                    </div>
                    {meetingLink && (
                      <a
                        href={meetingLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shrink-0"
                      >
                        <Video className="w-3.5 h-3.5" /> Join
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ═══ ACTIVE CASELOAD ═══ */}
      <div className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center"><Users className="w-4 h-4 text-primary" /></div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Active Caseload</h3>
              <p className="text-xs text-slate-500">{patients.length} patient{patients.length !== 1 ? 's' : ''} under your care</p>
            </div>
          </div>
          <button onClick={() => navigate('/health-log')} className="text-xs font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all">
            View all <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="divide-y divide-slate-50">
          {patients.length === 0 ? (
            <div className="p-8 text-center"><Users className="w-10 h-10 mx-auto mb-2 text-slate-200" /><p className="text-sm text-slate-500">No active patients</p></div>
          ) : patients.slice(0, 5).map((p) => {
            const patient = p.patientId || p;
            const name = patient.name || 'Patient';
            const priority = priorityPatients.find(pp => pp.patient?._id === (patient._id || patient));
            const riskScore = priority?.riskScore?.overall || 0;
            return (
              <button key={patient._id || p._id} onClick={() => navigate(`/patient/${patient._id || p._id}`)} className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left">
                <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=00478d&color=fff&size=36`} alt={name} className="w-9 h-9 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 break-words">{name}</p>
                  <p className="text-xs text-slate-500">{patient.email || ''}</p>
                </div>
                {riskScore > 0 && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${riskScore >= 70 ? 'bg-red-50 text-red-600' : riskScore >= 40 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    Risk {riskScore}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ ACTIVITY CALENDAR ═══ */}
      <div className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden">
        {/* Calendar header */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-indigo-500" /></div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Activity Timeline</h3>
              <p className="text-xs text-slate-500">Symptom reports, consultations, and follow-ups</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekOffset(0)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${weekOffset === 0 ? 'bg-primary text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-white'}`}>Today</button>
            <div className="flex items-center bg-white rounded-full border border-slate-200 overflow-hidden">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 hover:bg-slate-50"><ChevronLeft className="w-3.5 h-3.5 text-slate-500" /></button>
              <span className="px-3 text-xs font-bold text-slate-700 min-w-[150px] text-center">{weekLabel}</span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 hover:bg-slate-50"><ChevronRight className="w-3.5 h-3.5 text-slate-500" /></button>
            </div>
            <div className="flex bg-slate-50 border border-slate-200 rounded-full p-0.5">
              <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}>Week</button>
              <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}>Day</button>
            </div>
            <button onClick={() => setCalendarExpanded(true)} className="p-2 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors" title="Expand calendar">
              <Maximize2 className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
        </div>

        {viewMode === 'week' ? (
          <>
            {/* Week day headers */}
            <div className="grid grid-cols-8 border-b border-slate-100">
              <div className="p-3 text-xs font-bold text-slate-500 uppercase tracking-widest border-r border-slate-100">Time</div>
              {weekDates.map((date, i) => {
                const today = isToday(date);
                const dayEvts = getEventsForDay(date);
                return (
                  <button key={i} onClick={() => { setSelectedDay(i); setViewMode('day'); }} className={`p-3 text-center hover:bg-slate-50 transition-colors ${today ? 'bg-primary/5' : ''} ${i < 6 ? 'border-r border-slate-100' : ''}`}>
                    <p className="text-xs font-bold text-slate-500 uppercase">{CAL_DAYS[i]}</p>
                    <p className={`text-lg font-extrabold tracking-tighter mt-0.5 ${today ? 'text-primary' : 'text-slate-900'}`}>{date.getDate()}</p>
                    {dayEvts.length > 0 && (
                      <div className="flex justify-center gap-0.5 mt-1">
                        {dayEvts.slice(0, 3).map((e, j) => <span key={j} className={`w-1.5 h-1.5 rounded-full ${(sevCfg[e.severity] || sevCfg.medium).dot}`} />)}
                        {dayEvts.length > 3 && <span className="text-xs font-bold text-slate-500">+{dayEvts.length - 3}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Time grid */}
            <div className="max-h-[420px] overflow-y-auto">
              {HOURS.map(hour => (
                <div key={hour} className="grid grid-cols-8 border-b border-slate-50 min-h-[52px]">
                  <div className="p-2 text-xs font-bold text-slate-500 border-r border-slate-100 flex items-start">
                    {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                  </div>
                  {weekDates.map((date, dayIdx) => {
                    const slotEvts = getEventsForSlot(date, hour);
                    return (
                      <div key={dayIdx} className={`p-0.5 ${dayIdx < 6 ? 'border-r border-slate-50' : ''} ${isToday(date) ? 'bg-primary/[0.02]' : ''}`}>
                        {slotEvts.map(evt => {
                          if (evt.type === 'appointment') {
                            const cfg = APPT_CONFIG[evt.apptType] || APPT_CONFIG.virtual_visit;
                            const ApptIcon = cfg.icon;
                            return (
                              <div key={evt.id} className={`w-full text-left px-1.5 py-1 rounded-lg ${cfg.bg} ${cfg.border} border mb-0.5`}>
                                <div className="flex items-center gap-1">
                                  <ApptIcon className={`w-3 h-3 shrink-0 ${cfg.text}`} />
                                  <p className={`text-xs font-bold ${cfg.text} break-words leading-tight`}>{cfg.label}</p>
                                </div>
                                <p className="text-xs text-slate-500 break-words leading-tight">{evt.patient}</p>
                              </div>
                            );
                          }
                          const sev = sevCfg[evt.severity] || sevCfg.medium;
                          return (
                            <button key={evt.id} onClick={() => navigate(`/symptom/${evt.symptomId}`)} className={`w-full text-left px-1.5 py-1 rounded-lg ${sev.bg} ${sev.border} border mb-0.5 hover:shadow-sm transition-all cursor-pointer group`}>
                              <p className={`text-xs font-bold ${sev.text} break-words leading-tight group-hover:text-primary`}>{evt.title}</p>
                              <p className="text-xs text-slate-500 break-words leading-tight">{evt.patient}</p>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Day view header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedDay(d => Math.max(0, d - 1))} className="p-2 hover:bg-slate-50 rounded-lg"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{CAL_DAYS[selectedDay]}</p>
                  <p className="font-bold text-lg text-slate-900">{weekDates[selectedDay]?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <button onClick={() => setSelectedDay(d => Math.min(6, d + 1))} className="p-2 hover:bg-slate-50 rounded-lg"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
              </div>
              <button onClick={() => setViewMode('week')} className="text-xs font-bold text-primary hover:underline">Back to Week</button>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              {HOURS.map(hour => {
                const slotEvts = weekDates[selectedDay] ? getEventsForSlot(weekDates[selectedDay], hour) : [];
                return (
                  <div key={hour} className="flex border-b border-slate-50 min-h-[60px]">
                    <div className="w-20 shrink-0 p-3 text-xs font-bold text-slate-500 border-r border-slate-100 flex items-start">
                      {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                    </div>
                    <div className="flex-1 p-2 space-y-1.5">
                      {slotEvts.map(evt => {
                        if (evt.type === 'appointment') {
                          const cfg = APPT_CONFIG[evt.apptType] || APPT_CONFIG.virtual_visit;
                          const ApptIcon = cfg.icon;
                          return (
                            <div key={evt.id} className={`w-full text-left rounded-xl p-3 border ${cfg.bg} ${cfg.border} flex items-start gap-3`}>
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-white/60`}>
                                <ApptIcon className={`w-4 h-4 ${cfg.text}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className={`font-bold text-sm ${cfg.text} break-words`}>{cfg.label}</p>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white/60 ${cfg.text} uppercase shrink-0`}>Appt</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{evt.patient}</span>
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{evt.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                {evt.meetingLink && (
                                  <a href={evt.meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-xs font-bold text-blue-600 hover:underline">
                                    <Video className="w-3 h-3" /> Join call
                                  </a>
                                )}
                                {evt.notes && <p className="text-xs text-slate-500 mt-0.5 italic">{evt.notes}</p>}
                              </div>
                            </div>
                          );
                        }
                        const sev = sevCfg[evt.severity] || sevCfg.medium;
                        const EvtIcon = typeIcon[evt.type] || Activity;
                        return (
                          <button key={evt.id} onClick={() => navigate(`/symptom/${evt.symptomId}`)} className="w-full text-left bg-slate-50 rounded-xl p-3 border border-slate-100 hover:bg-white hover:shadow-sm transition-all group flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${sev.bg}`}><EvtIcon className={`w-4 h-4 ${sev.text}`} /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-bold text-sm text-slate-900 group-hover:text-primary break-words">{evt.title}</p>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.text} uppercase shrink-0`}>{evt.severity}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" />{evt.patient}</span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{evt.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Week summary below calendar */}
        <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span><span className="font-bold text-slate-700">{weekEvents.length}</span> events this week</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {weekEvents.filter(e => e.severity === 'urgent' || e.severity === 'high').length} critical</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {weekEvents.filter(e => e.type === 'consultation').length} consultations</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> {weekEvents.filter(e => e.type === 'appointment').length} appointments</span>
          </div>
        </div>
      </div>

      {/* ═══ EXPANDED CALENDAR MODAL ═══ */}
      {calendarExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setCalendarExpanded(false)} />
          <div className="relative w-[95vw] max-w-[1400px] h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
            {/* Modal header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-indigo-500" /></div>
                <div>
                  <h3 className="font-bold text-base text-slate-900">Activity Timeline</h3>
                  <p className="text-xs text-slate-500">Full calendar view</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setWeekOffset(0)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${weekOffset === 0 ? 'bg-primary text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-white'}`}>Today</button>
                <div className="flex items-center bg-white rounded-full border border-slate-200 overflow-hidden">
                  <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 hover:bg-slate-50"><ChevronLeft className="w-3.5 h-3.5 text-slate-500" /></button>
                  <span className="px-3 text-xs font-bold text-slate-700 min-w-[150px] text-center">{weekLabel}</span>
                  <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 hover:bg-slate-50"><ChevronRight className="w-3.5 h-3.5 text-slate-500" /></button>
                </div>
                <div className="flex bg-slate-50 border border-slate-200 rounded-full p-0.5">
                  <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}>Week</button>
                  <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}>Day</button>
                </div>
                <button onClick={() => setCalendarExpanded(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors ml-2" title="Close">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            {/* Modal calendar body */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {viewMode === 'week' ? (
                <>
                  <div className="grid grid-cols-8 border-b border-slate-100 shrink-0">
                    <div className="p-4 text-xs font-bold text-slate-500 uppercase tracking-widest border-r border-slate-100">Time</div>
                    {weekDates.map((date, i) => {
                      const today = isToday(date);
                      const dayEvts = getEventsForDay(date);
                      return (
                        <button key={i} onClick={() => { setSelectedDay(i); setViewMode('day'); }} className={`p-4 text-center hover:bg-slate-50 transition-colors ${today ? 'bg-primary/5' : ''} ${i < 6 ? 'border-r border-slate-100' : ''}`}>
                          <p className="text-xs font-bold text-slate-500 uppercase">{CAL_DAYS[i]}</p>
                          <p className={`text-xl font-extrabold tracking-tighter mt-0.5 ${today ? 'text-primary' : 'text-slate-900'}`}>{date.getDate()}</p>
                          {dayEvts.length > 0 && (
                            <div className="flex justify-center gap-0.5 mt-1">
                              {dayEvts.slice(0, 4).map((e, j) => <span key={j} className={`w-1.5 h-1.5 rounded-full ${(sevCfg[e.severity] || sevCfg.medium).dot}`} />)}
                              {dayEvts.length > 4 && <span className="text-xs font-bold text-slate-500">+{dayEvts.length - 4}</span>}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {HOURS.map(hour => (
                      <div key={hour} className="grid grid-cols-8 border-b border-slate-50 min-h-[70px]">
                        <div className="p-3 text-xs font-bold text-slate-500 border-r border-slate-100 flex items-start">
                          {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                        </div>
                        {weekDates.map((date, dayIdx) => {
                          const slotEvts = getEventsForSlot(date, hour);
                          return (
                            <div key={dayIdx} className={`p-1 ${dayIdx < 6 ? 'border-r border-slate-50' : ''} ${isToday(date) ? 'bg-primary/[0.02]' : ''}`}>
                              {slotEvts.map(evt => {
                                const sev = sevCfg[evt.severity] || sevCfg.medium;
                                return (
                                  <button key={evt.id} onClick={() => { setCalendarExpanded(false); navigate(`/symptom/${evt.symptomId}`); }} className={`w-full text-left px-2 py-1.5 rounded-lg ${sev.bg} ${sev.border} border mb-1 hover:shadow-sm transition-all cursor-pointer group`}>
                                    <p className={`text-xs font-bold ${sev.text} break-words leading-tight group-hover:text-primary`}>{evt.title}</p>
                                    <p className="text-xs text-slate-500 break-words leading-tight">{evt.patient}</p>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedDay(d => Math.max(0, d - 1))} className="p-2 hover:bg-slate-50 rounded-lg"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
                      <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{CAL_DAYS[selectedDay]}</p>
                        <p className="font-bold text-lg text-slate-900">{weekDates[selectedDay]?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      <button onClick={() => setSelectedDay(d => Math.min(6, d + 1))} className="p-2 hover:bg-slate-50 rounded-lg"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
                    </div>
                    <button onClick={() => setViewMode('week')} className="text-xs font-bold text-primary hover:underline">Back to Week</button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {HOURS.map(hour => {
                      const slotEvts = weekDates[selectedDay] ? getEventsForSlot(weekDates[selectedDay], hour) : [];
                      return (
                        <div key={hour} className="flex border-b border-slate-50 min-h-[70px]">
                          <div className="w-24 shrink-0 p-4 text-xs font-bold text-slate-500 border-r border-slate-100 flex items-start">
                            {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                          </div>
                          <div className="flex-1 p-2 space-y-1.5">
                            {slotEvts.map(evt => {
                              const sev = sevCfg[evt.severity] || sevCfg.medium;
                              const EvtIcon = typeIcon[evt.type] || Activity;
                              return (
                                <button key={evt.id} onClick={() => { setCalendarExpanded(false); navigate(`/symptom/${evt.symptomId}`); }} className="w-full text-left bg-slate-50 rounded-xl p-4 border border-slate-100 hover:bg-white hover:shadow-sm transition-all group flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sev.bg}`}><EvtIcon className={`w-5 h-5 ${sev.text}`} /></div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="font-bold text-sm text-slate-900 group-hover:text-primary break-words">{evt.title}</p>
                                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${sev.bg} ${sev.text} uppercase shrink-0`}>{evt.severity}</span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{evt.patient}</span>
                                      {evt.doctor && <span>Dr. {evt.doctor}</span>}
                                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{evt.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Modal footer */}
            <div className="p-4 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 shrink-0">
              <span><span className="font-bold text-slate-700">{weekEvents.length}</span> events this week</span>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {weekEvents.filter(e => e.severity === 'urgent' || e.severity === 'high').length} critical</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {weekEvents.filter(e => e.type === 'consultation').length} consultations</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {weekEvents.filter(e => e.type === 'follow-up').length} follow-ups</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-bold text-slate-700 mb-1">How your schedule works</p>
            <ul className="text-xs text-slate-600 space-y-0.5">
              <li>• On-call shifts are managed by your administrator</li>
              <li>• The timeline shows when symptoms, consultations, and follow-ups occurred</li>
              <li>• Click any event to view full details</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulePage;
